import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App, Table, Tag, Space, Input, Select, Button, Popconfirm } from 'antd';
import {
  ReconciliationOutlined,
  EyeOutlined,
  DeleteOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'libs/dayjs';
import { PageHeader } from 'components/headings';
import useReporteVisitaList from 'hooks/useReporteVisitaList';
import { deleteReporteVisita } from 'api/reporteVisita';
import DateRangeFilter from 'components/filters/DateRangeFilter';
import { useUser } from 'providers/Users/UserProvider';
import { useClientes } from 'providers/Clientes/ClientesProvider';
import ReporteVisitaKpiStrip from './ReporteVisitaKpiStrip';

const TIPO_LABEL = {
  levantamiento: 'Levantamiento',
  diagnostico: 'Diagnóstico',
  preinspeccion: 'Preinspección',
  cierre: 'Cierre',
  libre: 'Libre',
};

export default function ReportesVisitaPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { rows, total, loading, page, setPage, pageSize, setSearch, filters, setFilters, refresh } =
    useReporteVisitaList();
  const [reloadKey, setReloadKey] = useState(0);

  // Opciones para los filtros de técnico (RUT) y cliente (id). Los providers
  // ClientesProvider/UserProvider los monta withEjecutivoProvider en la ruta.
  const { especialistas, call: loadEspecialistas, hasCalled } = useUser();
  const { data: clientes, call: loadClientes } = useClientes();
  useEffect(() => {
    if (!hasCalled) loadEspecialistas();
  }, [hasCalled, loadEspecialistas]);
  useEffect(() => {
    loadClientes();
  }, [loadClientes]);

  const tecnicoOptions = useMemo(
    () =>
      (especialistas ?? [])
        .map((e) => ({ value: e.rut, label: e.fullname }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [especialistas]
  );
  const clienteOptions = useMemo(
    () =>
      (clientes ?? [])
        .map((c) => ({ value: c.id, label: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [clientes]
  );
  // RUT → nombre para mostrar el técnico legible en la tabla (no solo el RUT).
  const tecnicoNombre = useMemo(
    () => Object.fromEntries((especialistas ?? []).map((e) => [e.rut, e.fullname])),
    [especialistas]
  );

  const reloadAll = () => {
    refresh();
    setReloadKey((k) => k + 1);
  };

  const setTipo = (tipo) => setFilters((f) => ({ ...f, tipo }));
  const setEstado = (estado) => setFilters((f) => ({ ...f, estado }));
  const setTecnico = (rut) => {
    setFilters((f) => ({ ...f, creado_por: rut }));
    setPage(1);
  };
  const setCliente = (id) => {
    setFilters((f) => ({ ...f, cliente_id: id }));
    setPage(1);
  };
  const setRango = (dateFrom, dateTo) => {
    setFilters((f) => ({ ...f, dateFrom, dateTo }));
    setPage(1);
  };
  const rangoValue =
    filters.dateFrom || filters.dateTo
      ? [
          filters.dateFrom ? dayjs(filters.dateFrom) : null,
          filters.dateTo ? dayjs(filters.dateTo) : null,
        ]
      : null;

  const onDelete = async (id) => {
    try {
      await deleteReporteVisita(id);
      message.success('Reporte eliminado');
      reloadAll();
    } catch {
      message.error('No se pudo eliminar');
    }
  };

  const columns = [
    { title: 'N°', dataIndex: 'id', width: 70 },
    { title: 'Título', dataIndex: 'titulo' },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      render: (t) => <Tag>{TIPO_LABEL[t] || t}</Tag>,
    },
    {
      title: 'Técnico',
      dataIndex: 'created_by_rut',
      render: (rut) => tecnicoNombre[rut] || rut,
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      render: (e) => (
        <Tag color={e === 'draft' ? 'gold' : 'green'}>{e === 'draft' ? 'Borrador' : 'Cerrada'}</Tag>
      ),
    },
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      render: (d) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—'),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 130,
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/admin/reportes-visita/${r.id}`)}
          />
          <Popconfirm
            title="¿Eliminar reporte?"
            onConfirm={() => onDelete(r.id)}
            okText="Sí"
            cancelText="No"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <PageHeader
        title="Reportes de visita"
        subtitle="Visitas en terreno registradas por los técnicos"
        icon={ReconciliationOutlined}
        actions={
          <Button icon={<ReloadOutlined />} onClick={reloadAll}>
            Actualizar
          </Button>
        }
      />

      <ReporteVisitaKpiStrip active={filters.tipo} onPick={setTipo} reloadKey={reloadKey} />

      <Space wrap style={{ marginBottom: 12 }}>
        <Input.Search
          placeholder="Buscar por título…"
          allowClear
          onSearch={setSearch}
          style={{ width: 240 }}
        />
        <Select
          allowClear
          placeholder="Estado"
          style={{ width: 150 }}
          value={filters.estado}
          onChange={setEstado}
          options={[
            { value: 'draft', label: 'Borrador' },
            { value: 'cerrado', label: 'Cerrada' },
          ]}
        />
        <Select
          allowClear
          showSearch
          placeholder="Técnico"
          style={{ width: 200 }}
          value={filters.creado_por}
          onChange={setTecnico}
          options={tecnicoOptions}
          optionFilterProp="label"
        />
        <Select
          allowClear
          showSearch
          placeholder="Cliente"
          style={{ width: 220 }}
          value={filters.cliente_id}
          onChange={setCliente}
          options={clienteOptions}
          optionFilterProp="label"
        />
        <DateRangeFilter value={rangoValue} onChange={setRango} />
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: setPage,
          showSizeChanger: false,
        }}
      />
    </div>
  );
}
